// Shared client types, fetch helpers and the node vocabulary for panel-authored payout
// flows. Used by the registry page (flows-view.ts), the step editor (flow-editor-view.ts),
// the device element picker (flow-inspector.ts) and the launcher's flow dropdown.
//
// The vocabulary here MIRRORS the clicker's engine (mobile-clicker/common/flow_runner.py,
// KNOWN_OPS): it decides which fields the editor offers per op. The engine is the
// authority — it re-validates every flow before running it — so a mismatch shows up as a
// rejected flow at launch, never as a half-executed transfer. Keep the two in step.
import { t } from '../ui/lang';
export const TASK_TYPES = ['payout', 'deposit', 'auto', 'shift'];
/** The queues a `run_payout_flow` node drains — the platform's own task_type values. */
export const QUEUES = ['external_payout', 'merchant_payout'];
/**
 * How the palette and the type selector group the ops: what drives the phone, what steers
 * the flow, what ends a payout, what walks a list on screen, and what only a DEPOSIT flow
 * may do (talk to the platform's deposit callback and task queues).
 */
export const CATEGORIES = ['screen', 'flow', 'outcome', 'list', 'task', 'auto', 'shift', 'web'];
const svg = (body) => `<svg viewBox="0 0 24 24" aria-hidden="true">${body}</svg>`;
/**
 * How a payout that did not run clean is worded on the operation, per commit node.
 *
 * Two sentences, one for each side of the commit tap — the engine picks between them by
 * whether that tap had already happened, and the "after" one serves both a crash (it
 * becomes the reason) and a CANCEL a step asked for (it is appended to that step's
 * comment). The defaults are mirrored from flow_runner.py (MSG_BEFORE / MSG_AFTER) so the
 * editor can show, as a placeholder, exactly what will be written when the author leaves
 * the field alone — keep the two lists in step.
 */
export const COMMIT_MSG_FIELDS = ['msg_before', 'msg_after'];
/**
 * The commit's swipe fallback: the slider to drag, and what proves the drag went through.
 *
 * One wallet confirms the same transfer two ways depending on the recipient (LemonCash: a
 * tap button for another Lemon user's CVU, a swipe-to-confirm sheet for a plain bank CBU),
 * and a flow may hold only ONE commit — so both gestures live on this node: the tap `xpath`
 * is tried first and the slider is dragged when that button is not on screen.
 */
export const COMMIT_SWIPE_FIELDS = ['swipe_xpath', 'swipe_until'];
export const COMMIT_MSG_DEFAULTS = {
    msg_before: 'the transfer was never submitted (money did not move)',
    msg_after: 'the transfer had already been submitted — manual verification required',
};
export const OPS = {
    foreground: { category: 'screen', fields: ['package'], icon: svg('<path d="M12 3v9"/><path d="M6.3 7a8 8 0 1 0 11.4 0"/>') },
    activate: { category: 'screen', fields: ['package'], icon: svg('<path d="M20 12a8 8 0 1 1-2.3-5.6"/><path d="M20 4v4h-4"/>') },
    tap: { category: 'screen', fields: ['xpath', 'timeout'], icon: svg('<path d="M9 11V6a2 2 0 0 1 4 0v5"/><path d="M13 8a2 2 0 0 1 4 0v3"/><path d="M17 10a2 2 0 0 1 4 0v4a6 6 0 0 1-6 6h-2a6 6 0 0 1-5-2.7L5 15"/>') },
    fill: { category: 'screen', fields: ['xpath', 'value', 'timeout'], icon: svg('<path d="M4 7h11M4 12h7M4 17h9"/><path d="m15.5 15.5 5-5 2 2-5 5h-2z"/>') },
    // A Compose field that does not focus from one tap drops setText silently and stays
    // empty: this one re-taps until the field reports focus, then types.
    fill_focused: { category: 'screen', fields: ['xpath', 'value'], icon: svg('<path d="M4 7h11M4 12h7M4 17h6"/><circle cx="17" cy="15" r="4"/><path d="M17 11v-2M17 21v-2M21 15h2M11 15h2"/>') },
    keypad: { category: 'screen', fields: ['value', 'key_template', 'timeout'], icon: svg('<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 8h.01M12 8h.01M16 8h.01M8 12h.01M12 12h.01M16 12h.01M9 16h6"/>') },
    type_digits: { category: 'screen', fields: ['xpath', 'value', 'package'], icon: svg('<rect x="3" y="10" width="18" height="10" rx="2"/><path d="M7 14h.01M11 14h.01M15 14h.01M9 17h6"/><path d="M12 3v5"/>') },
    // The Android device-credential prompt (MercadoPago guards the app with it): that
    // screen rejects key injection, so the PIN goes in as one setText plus the IME's done.
    enter_pin: { category: 'screen', fields: ['xpath', 'value'], icon: svg('<rect x="4" y="10" width="16" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/><path d="M9 15h.01M12 15h.01M15 15h.01"/>') },
    read_balance: { category: 'screen', fields: ['xpath', 'attr', 'parse', 'timeout'], icon: svg('<circle cx="12" cy="12" r="9"/><path d="M12 7v10M9.5 9.5h4a1.8 1.8 0 0 1 0 3.5h-3a1.8 1.8 0 0 0 0 3.5h4"/>') },
    read_account: { category: 'screen', fields: ['xpath', 'attr', 'regex', 'timeout'], icon: svg('<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18M7 15h5"/>') },
    swipe_refresh: { category: 'screen', fields: [], icon: svg('<path d="M20 12a8 8 0 1 1-2.3-5.6M20 4v4h-4"/>') },
    // A vertical drag in fractions of the screen height: what a half-collapsed sheet needs
    // before the rows under its fold exist to be read at all.
    swipe: { category: 'screen', fields: ['swipe_drag'], icon: svg('<path d="M12 20V5"/><path d="m7 10 5-5 5 5"/><path d="M5 20h14"/>') },
    back: { category: 'screen', fields: ['times'], icon: svg('<path d="M19 12H5M12 19l-7-7 7-7"/>') },
    screenshot: { category: 'screen', fields: ['name', 'replace'], icon: svg('<path d="M4 8h3l1.5-2h7L17 8h3v11H4z"/><circle cx="12" cy="13" r="3.2"/>') },
    // A USSD question put to the operator (the balance, a status): no money moves.
    ussd: { category: 'screen', fields: ['ussd', 'ussd_fail', 'regex', 'var', 'timeout'], icon: svg('<rect x="6" y="2" width="12" height="20" rx="2"/><path d="M10 18h4"/><path d="M9 7h6M9 11h4"/>') },
    sleep: { category: 'screen', fields: ['minmax'], icon: svg('<circle cx="12" cy="12" r="9"/><path d="M12 8v4l3 2"/>') },
    wait: { category: 'flow', fields: ['xpath', 'timeout', 'on_absent'], icon: svg('<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>') },
    if_visible: { category: 'flow', fields: ['xpath', 'timeout'], branches: ['then', 'else'], icon: svg('<path d="M6 3v6a4 4 0 0 0 4 4h8"/><path d="M6 21v-6"/><path d="m15 10 3 3-3 3"/>') },
    if_account_cached: { category: 'flow', fields: [], branches: ['then', 'else'], icon: svg('<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18"/><path d="m8 16 2 2 4-4"/>') },
    if_app_running: { category: 'flow', fields: ['package'], branches: ['then', 'else'], icon: svg('<rect x="4" y="3" width="16" height="18" rx="2"/><path d="m10 9 5 3-5 3z"/>') },
    repeat: { category: 'flow', fields: ['times', 'until', 'timeout'], branches: ['do'], icon: svg('<path d="M17 2l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="M7 22l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>') },
    goto: { category: 'flow', fields: ['target'], icon: svg('<path d="M5 12h14"/><path d="m13 6 6 6-6 6"/><circle cx="4" cy="12" r="1.6"/>') },
    commit: { category: 'outcome', fields: ['xpath', 'timeout', 'commit_swipe', 'commit_msgs'], icon: svg('<path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z"/>') },
    verify_success: { category: 'outcome', fields: ['xpath', 'timeout', 'on_absent'], icon: svg('<circle cx="12" cy="12" r="9"/><path d="m8.2 12.4 2.6 2.6 5-5.4"/>') },
    outcome: { category: 'outcome', fields: ['outcome'], icon: svg('<path d="M5 21V4"/><path d="M5 5h6l1 2h7v8h-7l-1-2H5"/>') },
    // The transfer by USSD from the phone's own SIM: dialling is the commit, and any
    // non-empty reply from the network is the success check — unless a wait_sms follows,
    // which then decides by the operator's SMS (flow_runner._op_ussd_pay / _op_wait_sms).
    ussd_pay: { category: 'outcome', fields: ['ussd', 'ussd_pay', 'ussd_fail', 'regex', 'var', 'timeout', 'commit_msgs'], icon: svg('<rect x="6" y="2" width="12" height="20" rx="2"/><path d="M10 18h4"/><path d="M13 5 9.5 11H13l-1 4 3.5-6H12l1-4z"/>') },
    wait_sms: { category: 'outcome', fields: ['sms'], icon: svg('<path d="M4 5h16v11H9l-5 4z"/><path d="m8.5 10.5 2 2 4-4"/>') },
    for_each: { category: 'list', fields: ['xpath', 'max_scrolls'], branches: ['do'], icon: svg('<path d="M4 6h10M4 12h10M4 18h10"/><path d="M18 4v16"/><path d="m15 17 3 3 3-3"/>') },
    read: { category: 'list', fields: ['xpath', 'attr', 'regex', 'var', 'timeout'], icon: svg('<path d="M4 7h16M4 12h10"/><path d="M15 15h5v5"/><path d="m20 15-5 5"/>') },
    if_match: { category: 'list', fields: ['value', 'regex'], branches: ['then', 'else'], icon: svg('<path d="M6 3v6a4 4 0 0 0 4 4h8"/><path d="M6 21v-6"/><path d="M14 8h6M14 12h6"/>') },
    break: { category: 'list', fields: [], icon: svg('<path d="M4 6h10M4 12h7M4 18h4"/><path d="m15 15 5 5M20 15l-5 5"/>') },
    if_new: { category: 'task', fields: ['key'], branches: ['then', 'else'], icon: svg('<path d="M6 3v6a4 4 0 0 0 4 4h8"/><path d="M6 21v-6"/><path d="M17 8V4M15 6h4"/>') },
    // The deposits the platform is waiting for: one read, and the answer branches — `then`
    // when it came back with something, `else` when the platform expects nothing. The list
    // itself stays in the pass's context; walking it is not this step's job.
    take_deposits: { category: 'task', fields: ['deposit_filters'], branches: ['then', 'else'], icon: svg('<path d="M4 4h16v6H4z"/><path d="M8 7h.01"/><path d="M12 20v-6"/><path d="m9 17 3-3 3 3"/>') },
    report_deposit: { category: 'task', fields: ['deposit'], icon: svg('<path d="M12 3v11"/><path d="m8 10 4 4 4-4"/><path d="M4 17v3h16v-3"/>') },
    // One step, no lanes: draining a queue is the engine's own bookkeeping (the batch
    // request, marking each task in work, the report back), and all the node
    // says is which queue and which payout flow pays its tasks out.
    run_payout_flow: { category: 'task', fields: ['task_type', 'flow_id'], icon: svg('<path d="M4 4h16v6H4z"/><path d="M4 14h16v6H4z"/><path d="M8 7h.01M8 17h.01"/>') },
    // A payout flow's own request: the queue's tasks, each paid out through the steps below it.
    take_tasks: { category: 'task', fields: ['task_type'], icon: svg('<path d="M4 4h16v6H4z"/><path d="M8 7h.01"/><path d="M12 14v6"/><path d="m9 17 3 3 3-3"/>') },
    // An auto flow's own step: play a whole deposit flow here. It is what reads the balance,
    // so it stands before the queues — and its own `run_payout_flow` steps do not run, because
    // in an auto pass the queues are this document's.
    // The device's balance band as a condition: `then` while it is open, `else` while it is
    // shut. Judged against the balance the deposit flow above it read, so the wallet switches
    // between collecting and paying out inside one pass.
    if_balance_band: { category: 'auto', fields: [], branches: ['then', 'else'], icon: svg('<path d="M4 8h16"/><path d="M4 16h16"/><path d="m9 5 3-2 3 2"/><path d="m15 19-3 2-3-2"/>') },
    run_deposit_flow: { category: 'auto', fields: ['flow_id'], icon: svg('<path d="M12 3v11"/><path d="m8 10 4 4 4-4"/><path d="M4 17v3h16v-3"/><path d="M20 7a8 8 0 0 0-8-4"/>') },
    // «Close shift» flows only: start filming. From here on the for_each rows are filmed and
    // only their date is judged against the shift (flow_runner._run_for_each / shift_recorder).
    record_start: { category: 'shift', fields: [], icon: svg('<rect x="3" y="5" width="13" height="14" rx="2"/><path d="m16 10 5-3v10l-5-3"/><circle cx="9.5" cy="12" r="2"/>') },
    // ── Web (platform "web") ──────────────────────────────────────────────────
    // Wait until the tab's current_url matches a regex; absent within the timeout ends the pass.
    wait_url: { category: 'web', fields: ['match', 'timeout', 'on_absent'], icon: svg('<circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18"/>') },
    // Branch on the tab's current_url — used to detect a dropped session (redirect to login).
    if_url: { category: 'web', fields: ['match'], branches: ['then', 'else'], icon: svg('<circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="m15 9 3 3-3 3"/>') },
    // A full page reload. NOTE: session-scoped SPAs (ING) drop their auth on reload — refresh
    // the list in-app instead (a search/filter tap).
    reload: { category: 'web', fields: [], icon: svg('<path d="M20 12a8 8 0 1 1-2.3-5.6M20 4v4h-4"/>') },
    // Scroll an element into view (xpath) or the window down a viewport.
    scroll: { category: 'web', fields: ['xpath', 'timeout'], icon: svg('<path d="M12 5v14"/><path d="m6 13 6 6 6-6"/>') },
    // A keypad whose keys are pictures (ING's shuffled access-code pad): `xpath` selects the
    // digit cells, each key's picture is read against the named glyph set, then clicked.
    keypad_img: { category: 'web', fields: ['xpath', 'value', 'glyphs', 'timeout'], icon: svg('<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 8h.01M12 8h.01M16 8h.01M8 12h.01M12 12h.01M16 12h.01M9 16h6"/>') },
    // A Telegram alert to the operators' chat, throttled per device by `key` — the dropped-
    // session alert. Any platform may use it.
    notify_tg: { category: 'web', fields: ['text', 'key', 'cooldown_sec', 'tag_operator'], icon: svg('<path d="M21 4 3 11l6 2 2 6 3-4 4 3z"/>') },
};
/**
 * The steps that touch money or close a task — the only ones the editor marks out.
 * Everything else carries the same neutral stripe, so the eye lands on the transfer tap,
 * what licenses a SUCCESS, what ends the operation, the task request and the deposit report.
 */
export const CRITICAL_OPS = ['commit', 'verify_success', 'ussd_pay', 'wait_sms', 'outcome', 'take_tasks', 'report_deposit'];
/** The steps that move the money, and the ones that may license a SUCCESS — `ussd_pay` is
 *  both when no `wait_sms` follows it. Mirrors flow_runner._COMMIT_OPS / _SUCCESS_OPS. */
export const COMMIT_OPS = ['commit', 'ussd_pay'];
export const SUCCESS_OPS = ['verify_success', 'ussd_pay', 'wait_sms'];
export const USSD_OPS = ['ussd', 'ussd_pay'];
/** What a USSD payout code must carry (ussd_dialog.ACCOUNT_TOKEN / AMOUNT_TOKEN_RE). */
const USSD_ACCOUNT_TOKEN = '{ACCOUNT}';
const USSD_AMOUNT_TOKEN_RE = /\{AMOUNT(?::[^{}]*)?\}/;
/** PayoutHub's MTN MoMo Cameroon code; Cameroon tasks carry whole FCFA, hence `raw`. */
export const USSD_PAY_DEFAULT = '#149*1*{AMOUNT:raw}*{ACCOUNT}*{PIN}#';
/** What a new "Wait for SMS" step pre-fills: the GoIP Cameroon patterns (common/sms_patterns.py, MTN + Orange). */
export const SMS_SUCCESS_DEFAULT = [
    String.raw `Depot vers\s+{ACCOUNT}(?!\d)[\s\S]*?from\s+{ACCOUNT_FROM}(?!\d)[\s\S]*?(?<!\d){AMOUNT}FCFA`,
    String.raw `You have transferred\s+(?<!\d){AMOUNT}(?!\d)\s*XAF\s+to[\s\S]*?{ACCOUNT}(?!\d)`,
    String.raw `Transfert de\s+(?<!\d){AMOUNT}(?!\d)\s*FCFA\s+effectu[eé] avec succ[eè]s a[\s\S]*?{ACCOUNT}(?!\d)`,
    String.raw `D[eé]p[oô]t de\s+(?<!\d){AMOUNT}(?!\d)\s*XAF[\s\S]*?{ACCOUNT}(?!\d)[\s\S]*?effectu[eé] avec succ[eè]s`,
    String.raw `Cash in of\s+(?<!\d){AMOUNT}(?!\d)\s*XAF[\s\S]*?{ACCOUNT}(?!\d)[\s\S]*?has been successfully completed`,
];
export const SMS_DECLINE_DEFAULT = [
    String.raw `[EÉ]chec\s+de\s+la\s+transaction[\s.]*(?:Motif\s*:\s*)?(?P<reason>[^.]+)`,
];
/** What a new USSD step starts with as error-reply patterns: Android's own "call failed"
 *  boxes, drawn by the same phone packages as a real reply (both USSD ops)… */
export const USSD_FAIL_SYSTEM = [
    String.raw `сбой вызова|вызов не выполнен|проблема с подключением|код mmi`,
    String.raw `call failed|connection problem|invalid mmi|mmi code|ussd code running`,
    String.raw `[eé]chec de l.appel|probl[eè]me de connexion|code mmi`,
];
/** …and, for a transfer, the operator saying no. */
export const USSD_FAIL_OPERATOR = [
    String.raw `insuffisant|insufficient|недостаточно`,
    String.raw `[EÉ]chec|failed|erreur|error|ошибка|refus|rejet`,
];
/** flow_runner.DEFAULT_SMS_TIMEOUT. */
export const SMS_TIMEOUT_DEFAULT = 60;
/** Ops a deposit flow may use — and, for the queues, an auto flow. */
export const DEPOSIT_ONLY_OPS = ['if_new', 'report_deposit', 'take_deposits', 'run_payout_flow'];
/** …and the one only an auto flow may use. */
export const AUTO_ONLY_OPS = ['run_deposit_flow', 'if_balance_band'];
/** …the ones only a «close shift» flow may use… */
export const SHIFT_ONLY_OPS = ['record_start'];
/** Platform split, mirroring flow_runner._WEB_ONLY_OPS / _PHONE_ONLY_OPS. A web flow may not
 *  carry phone ops and a phone flow may not carry web ops; `foreground` / `notify_tg` are on
 *  neither list (both platforms, and any flow, respectively). */
export const WEB_ONLY_OPS = ['wait_url', 'if_url', 'reload', 'scroll', 'keypad_img'];
export const PHONE_ONLY_OPS = ['activate', 'keypad', 'enter_pin', 'type_digits', 'fill_focused',
    'ussd', 'ussd_pay', 'wait_sms', 'swipe_refresh', 'swipe', 'back'];
/** …and what a «close shift» flow may never hold: the money, the platform's tasks and queues
 *  (mirrors flow_runner._SHIFT_FORBIDDEN_OPS). `outcome` is allowed: it ends the run. */
export const SHIFT_FORBIDDEN_OPS = ['commit', 'verify_success', 'ussd_pay', 'wait_sms', 'take_tasks',
    'if_new', 'report_deposit', 'take_deposits', 'run_payout_flow', 'run_deposit_flow', 'if_balance_band'];
/** …and the one only a payout flow may use: its own tasks request. */
export const PAYOUT_ONLY_OPS = ['take_tasks'];
/**
 * Every op an auto flow may hold: the deposit pass, the queues, the band around them — and a
 * balance read of its own, for the pass whose deposit flow did not reach one (the band needs
 * a figure, and without it the wallet sits in `else` forever).
 */
export const AUTO_OPS = ['run_deposit_flow', 'run_payout_flow', 'if_balance_band', 'read_balance'];
/** Which kind of flow an op's `flow_id` names — the mark that resolves `default` is per
 *  (app, task type), so the two kinds are picked, shipped and embedded separately. */
export const FLOW_PICK_KIND = {
    run_deposit_flow: 'deposit',
    run_payout_flow: 'payout',
};
/** The filters a `take_deposits` node asks the deposit list with, in the editor's order.
 *  All optional: left empty, the account is the wallet this pass is on and the group and
 *  the bucket are the device's configured ones. */
export const DEPOSIT_FILTER_FIELDS = ['limit', 'account_number', 'operators_group', 'bucket_code'];
/** The fields of a `report_deposit` node, in the order the editor shows them. */
export const DEPOSIT_FIELDS = ['amount', 'sender', 'sender_number', 'external_id', 'datetime', 'key', 'currency', 'bank_code'];
/** Value tokens the engine resolves from the task; anything else is a literal. */
export const VALUE_TOKENS = ['ACCOUNT', 'AMOUNT', 'AMOUNT:raw', 'PIN', 'PASSWORD',
    'PASSWORD_LOGIN', 'PASSWORD_SECRET', 'LOGIN', 'CREDENTIAL', 'ROW'];
/**
 * Tokens that only make sense inside an xpath: the amount as the app RENDERS it, which is
 * how a flow finds the transaction row it has just created (AstroPay prints the same
 * transfer as "-22,000.00 ARS", "-22.000,00 ARS" and "-22 000,00 ARS" with an NBSP).
 * Which is why the amount is ONE token with a format after the colon rather than a token
 * per spelling — see AMOUNT_FORMATS.
 */
/** The non-breaking space some wallets group thousands with (AstroPay's account detail). */
export const NBSP = '\u00a0';
export const XPATH_TOKENS = ['ACCOUNT', 'ACCOUNT_FROM', 'BALANCE', 'AMOUNT:0',
    'AMOUNT:#,##0.00', 'AMOUNT:#.##0,00', `AMOUNT:#${NBSP}##0,00`, 'OPERATION_ID', 'ROW_XPATH'];
/**
 * Every token the engine substitutes (flow_runner._render), in reference order: what the
 * task carries, then the amount, then what the run itself produced, then what a deposit
 * flow's own nodes fill in (the row being walked, the deposit key, the task a lane runs
 * for). The two lists above are the SUGGESTIONS per field; this is the full table the
 * editor documents. Mirrors flow_runner.Var — keep the two in step.
 */
export const TOKEN_GROUPS = [
    { key: 'task', tokens: ['ACCOUNT', 'PIN', 'PASSWORD', 'PASSWORD_LOGIN', 'PASSWORD_SECRET', 'LOGIN',
            'CREDENTIAL', 'OPERATION_ID', 'DEVICE_ID'] },
    { key: 'amount', tokens: ['AMOUNT'] },
    { key: 'run', tokens: ['BALANCE', 'ACCOUNT_FROM', 'YEAR', 'USSD_REPLY', 'SMS_TEXT'] },
    { key: 'device', tokens: ['MIN_BALANCE', 'MAX_BALANCE', 'MIN_INITIAL_AMOUNT', 'MAX_INITIAL_AMOUNT',
            'OPERATORS_GROUP', 'BUCKET_CODE'] },
    { key: 'row', tokens: ['ROW', 'ROW_XPATH', 'ROW_INDEX', 'DEPOSIT_KEY', 'DEPOSITS_COUNT'] },
    { key: 'lane', tokens: ['TASK_TYPE', 'RECIPIENT_NAME', 'EXTERNAL_BANK', 'EXTERNAL_DOCUMENT'] },
];
/**
 * The amount's formats, as the Info tab lists them: what you write after the colon, what
 * it means, and what 123456 (a task's minor units — 1234.56) comes out as.
 *
 * Two kinds, and the Info tab keeps them apart because they are read differently. A NAMED
 * spec is a word you either know or don't — only the two that are not a spelling of the
 * screen are left (what you type, and the raw integer). Everything the screen PRINTS is a
 * PICTURE, read off itself: `0`/`#` are the digits and the picture's own punctuation is
 * the output's, so its row only has to say which separator does what. The engine still
 * answers to the old `minors` / `cents` / `cents2` / `nbsp` names (flow_runner._amount), but the
 * catalog does not offer them: a picture with the same separators says the same thing and
 * reads as the number on the screen. A non-breaking space is a separator like any other —
 * copy the row, the space in it is the non-breaking one.
 *
 * Mirrors flow_runner._amount; keep the two in step.
 */
export const AMOUNT_FORMATS = [
    { spec: '', kind: 'named', doc: 'pf.fmt_default', example: '1234,56' },
    { spec: 'raw', kind: 'named', doc: 'pf.fmt_raw', example: '123456' },
    { spec: '0', kind: 'picture', doc: 'pf.fmt_pic_plain', example: '1234' },
    { spec: '#,##0', kind: 'picture', doc: 'pf.fmt_pic_comma', example: '1,234' },
    { spec: '#.##0', kind: 'picture', doc: 'pf.fmt_pic_dot', example: '1.234' },
    { spec: `#${NBSP}##0`, kind: 'picture', doc: 'pf.fmt_pic_nbsp', example: `1${NBSP}234` },
    { spec: '#,##0.00', kind: 'picture', doc: 'pf.fmt_pic_comma_dot', example: '1,234.56' },
    { spec: '#.##0,00', kind: 'picture', doc: 'pf.fmt_pic_dot_comma', example: '1.234,56' },
    { spec: `#${NBSP}##0,00`, kind: 'picture', doc: 'pf.fmt_pic_nbsp_comma', example: `1${NBSP}234,56` },
    { spec: '0.00', kind: 'picture', doc: 'pf.fmt_pic_dot_cents', example: '1234.56' },
    { spec: '0,00', kind: 'picture', doc: 'pf.fmt_pic_comma_cents', example: '1234,56' },
];
/**
 * The amount tokens flows were written with before `{AMOUNT:format}`. The engine still
 * answers to them, but the editor rewrites a document on load so there is one spelling on
 * screen — and the stored flows were rewritten once by a migration (panel/src/server/db.ts).
 */
const LEGACY_AMOUNT = {
    AMOUNT_KEYPAD: 'AMOUNT',
    AMOUNT_MINORS: 'AMOUNT:raw',
    AMOUNT_WHOLE: 'AMOUNT:0',
    AMOUNT_CENTS: 'AMOUNT:cents',
    AMOUNT_CENTS2: 'AMOUNT:cents2',
    AMOUNT_GROUP_COMMA: 'AMOUNT:#,##0',
    AMOUNT_GROUP_DOT: 'AMOUNT:#.##0',
    AMOUNT_GROUP_NBSP: 'AMOUNT:nbsp',
};
/** What a token stands in for; '' for one the engine has but the catalog doesn't document. */
export function tokenDoc(token) {
    const key = `pf.tok_${token}`;
    const s = t(key);
    return s === key ? '' : s;
}
// What the outcome pickers offer. SUCCESS is deliberately NOT here: a run is successful
// because it reached the commit tap and played to the end, and the engine says so on its
// own. A step claiming SUCCESS short-circuits that — it raises the terminal directly, past
// the "finished without reaching commit" check — so a Finish step standing before commit
// would close an unpaid task as paid. MANUAL_REVIEW is out for a different reason: it is an
// alias of CANCEL, not a fourth status, so offering both only asked the author to choose
// between two spellings of the same thing. The engine still takes both — see LEGACY_OUTCOMES.
export const OUTCOMES = ['CANCEL', 'DECLINE'];
/** Outcomes the engine still takes but the picker no longer offers, so old flows stay valid. */
export const LEGACY_OUTCOMES = ['SUCCESS', 'MANUAL_REVIEW'];
/** An outcome the engine will accept — pickable or left over from an older flow. */
export function isOutcome(value) {
    const v = String(value ?? '');
    return OUTCOMES.includes(v) || LEGACY_OUTCOMES.includes(v);
}
/**
 * Options for an outcome `<select>`: the pickable ones, plus whatever the node already holds.
 *
 * A select whose value is not among its options renders with nothing selected and silently
 * rewrites the node on the first touch — so a flow still saying MANUAL_REVIEW keeps showing
 * it, and only an author who picks something else changes it.
 */
export function outcomeOptions(current) {
    const v = String(current ?? '');
    return v && !OUTCOMES.includes(v) ? [...OUTCOMES, v] : OUTCOMES;
}
/** Human label for an op — falls back to the raw op so an engine-only op still renders. */
export function opLabel(op) {
    const key = `pf.op_${op}`;
    const label = t(key);
    return label === key ? op : label;
}
/** Walk a node list depth-first, including branch lanes. */
export function walkNodes(nodes, fn) {
    for (const n of nodes ?? []) {
        fn(n);
        walkNodes(n.then, fn);
        walkNodes(n.else, fn);
        walkNodes(n.do, fn);
    }
}
/**
 * Where an op may stand (flow_runner._check_nesting): `break` in a loop, and — in a deposit
 * flow — no money steps at all: the transfer is the payout flow `run_payout_flow` plays.
 */
function nestingProblems(nodes, deposit, out, inLoop = false) {
    for (const n of nodes) {
        if (n.op === 'break' && !inLoop)
            out.push(t('pf.err_break_outside'));
        if (deposit && (COMMIT_OPS.includes(n.op) || SUCCESS_OPS.includes(n.op))) {
            out.push(t('pf.err_money_in_deposit', { op: opLabel(n.op) }));
        }
        for (const lane of OPS[n.op]?.branches ?? []) {
            nestingProblems(n[lane] ?? [], deposit, out, inLoop || n.op === 'for_each');
        }
    }
}
/** A USSD step's own rules (flow_runner._validate_ussd). */
function ussdProblems(n, out) {
    const code = String(n.ussd ?? '').trim();
    if (!code) {
        out.push(t('pf.err_ussd_code', { op: opLabel(n.op) }));
        return;
    }
    if (n.op === 'ussd_pay' && (!code.includes(USSD_ACCOUNT_TOKEN) || !USSD_AMOUNT_TOKEN_RE.test(code))) {
        out.push(t('pf.err_ussd_slots'));
    }
    for (const key of ['timeout']) {
        const raw = String(n[key] ?? '').trim();
        if (raw && !(/^\d+$/.test(raw) && Number(raw) > 0))
            out.push(t('pf.err_ussd_number', { op: opLabel(n.op), field: key }));
    }
}
/** The SMS wait's own rules and its place (flow_runner._validate_sms_node / _validate_sms_order). */
function smsProblems(nodes, out) {
    const order = [];
    walkNodes(nodes, (n) => {
        order.push(n.op);
        if (n.op !== 'wait_sms')
            return;
        const raw = String(n.sms_timeout ?? '').trim();
        if (raw && !(/^\d+$/.test(raw) && Number(raw) > 0))
            out.push(t('pf.err_ussd_number', { op: opLabel(n.op), field: 'sms_timeout' }));
        if (!(n.sms_success ?? []).some((p) => String(p).trim()))
            out.push(t('pf.err_sms_success'));
    });
    if (!order.includes('wait_sms'))
        return;
    if (order.filter((op) => op === 'wait_sms').length > 1)
        out.push(t('pf.err_sms_count'));
    const pay = order.indexOf('ussd_pay');
    if (pay < 0 || pay > order.indexOf('wait_sms'))
        out.push(t('pf.err_sms_order'));
}
/** Which top-level step holds `target` — itself or somewhere in its branches. */
function topLevelIndexOf(nodes, target) {
    return nodes.findIndex((step) => { let hit = false; walkNodes([step], (n) => { if (n === target)
        hit = true; }); return hit; });
}
/**
 * A payout flow that takes its own tasks (flow_runner._validate_take_tasks): one request, at
 * the top level, with the money below it — the steps after it are the lane played per task,
 * so a commit above it would run with no task in hand — and no jump across the split, since
 * the pass and the lane are run as two lists.
 */
function takeTasksProblems(nodes, out) {
    const own = [];
    walkNodes(nodes, (n) => { if (n.op === 'take_tasks')
        own.push(n); });
    if (!own.length)
        return;
    if (own.length > 1)
        out.push(t('pf.err_take_tasks_count'));
    const node = own[0];
    if (!nodes.includes(node)) {
        out.push(t('pf.err_take_tasks_nested'));
        return;
    }
    const split = topLevelIndexOf(nodes, node);
    walkNodes(nodes, (n) => {
        if (COMMIT_OPS.includes(n.op) && topLevelIndexOf(nodes, n) <= split)
            out.push(t('pf.err_take_tasks_commit_above'));
    });
    const indexOf = new Map(nodes.map((n, i) => [n.id, i]).filter(([id]) => !!id));
    walkNodes(nodes, (n) => {
        if (n.op !== 'goto')
            return;
        const here = topLevelIndexOf(nodes, n);
        const there = indexOf.get(n.target) ?? -1;
        if ((here <= split) !== (there <= split))
            out.push(t('pf.err_take_tasks_goto', { target: String(n.target ?? '') }));
    });
}
/**
 * The blocking problems the clicker's engine would reject the flow for. Mirrors
 * flow_runner.validate() so an author sees them here instead of at launch. `taskType` is
 * the record's kind: a deposit flow has no commit of its own — its task lanes are checked
 * as payouts instead.
 */
/** Is `op` anywhere in these steps — lanes included, where a banded queue step now sits. */
function hasOp(nodes, op) {
    let found = false;
    walkNodes(nodes, (n) => { if (n.op === op)
        found = true; });
    return found;
}
function everyOpIn(nodes, allowed) {
    let ok = true;
    walkNodes(nodes, (n) => { if (!allowed.includes(n.op))
        ok = false; });
    return ok;
}
export function flowProblems(doc, taskType = 'payout') {
    const out = [];
    const nodes = doc?.steps ?? [];
    const deposit = taskType === 'deposit';
    const auto = taskType === 'auto';
    const shift = taskType === 'shift';
    const web = doc?.platform === 'web';
    if (!doc?.app?.trim())
        out.push(t('pf.err_app_required'));
    if (shift && nodes.length) {
        let starts = 0;
        walkNodes(nodes, (n) => { if (n.op === 'record_start')
            starts += 1; });
        if (starts !== 1)
            out.push(t('pf.err_shift_record_start'));
        if (!hasOp(nodes, 'for_each'))
            out.push(t('pf.err_shift_no_walk'));
    }
    if (!nodes.length)
        out.push(t('pf.err_steps_empty'));
    // An auto flow composes whole documents: the deposit pass, then the queues. It holds no
    // steps of its own — those belong in the flows it names.
    if (auto && nodes.length) {
        if (!everyOpIn(nodes, AUTO_OPS))
            out.push(t('pf.err_auto_ops'));
        if (!nodes.some((n) => n.op === 'run_deposit_flow'))
            out.push(t('pf.err_auto_no_deposit'));
        if (!hasOp(nodes, 'run_payout_flow'))
            out.push(t('pf.err_auto_no_payout'));
        // The band is judged against the balance, and the balance is read by the deposit
        // pass — above it there is no figure to judge against.
        const firstDeposit = nodes.findIndex((n) => n.op === 'run_deposit_flow');
        const bandAbove = nodes.findIndex((n) => n.op === 'if_balance_band');
        if (bandAbove >= 0 && firstDeposit >= 0 && bandAbove < firstDeposit) {
            out.push(t('pf.err_auto_band_order'));
        }
    }
    let commits = 0;
    let successChecks = 0;
    walkNodes(nodes, (n) => {
        const spec = OPS[n.op];
        if (!spec) {
            out.push(t('pf.err_unknown_op', { op: n.op }));
            return;
        }
        // The queues belong to both kinds — a deposit flow drains them itself, an auto flow
        // is the queues plus the pass that reads the balance.
        if (DEPOSIT_ONLY_OPS.includes(n.op) && !deposit && !shift && !(auto && AUTO_OPS.includes(n.op))) {
            out.push(t('pf.err_deposit_only', { op: opLabel(n.op) }));
        }
        if (AUTO_ONLY_OPS.includes(n.op) && !auto)
            out.push(t('pf.err_auto_only', { op: opLabel(n.op) }));
        if (SHIFT_ONLY_OPS.includes(n.op) && !shift)
            out.push(t('pf.err_shift_only', { op: opLabel(n.op) }));
        if (shift && SHIFT_FORBIDDEN_OPS.includes(n.op))
            out.push(t('pf.err_shift_forbidden', { op: opLabel(n.op) }));
        if (PAYOUT_ONLY_OPS.includes(n.op) && (deposit || auto || shift))
            out.push(t('pf.err_payout_only', { op: opLabel(n.op) }));
        if (spec.fields.includes('xpath') && !String(n.xpath ?? '').trim()) {
            out.push(t('pf.err_no_xpath', { op: opLabel(n.op) }));
        }
        if (n.op === 'keypad' && !String(n.key_template ?? '').trim()) {
            out.push(t('pf.err_keypad_template'));
        }
        if (n.op === 'keypad_img' && !String(n.glyphs ?? '').trim()) {
            out.push(t('pf.err_keypad_glyphs'));
        }
        if (n.op === 'outcome' && !isOutcome(n.outcome)) {
            out.push(t('pf.err_outcome', { op: opLabel(n.op) }));
        }
        if (n.op === 'read' && !String(n.var ?? '').trim())
            out.push(t('pf.err_read_var'));
        if (n.op === 'if_new' && !String(n.key ?? '').trim())
            out.push(t('pf.err_new_key'));
        if (n.op === 'notify_tg' && !String(n.text ?? '').trim())
            out.push(t('pf.err_notify_text'));
        // Platform split: a web flow may not carry phone ops and vice versa.
        if (web && PHONE_ONLY_OPS.includes(n.op))
            out.push(t('pf.err_phone_in_web', { op: opLabel(n.op) }));
        if (!web && WEB_ONLY_OPS.includes(n.op))
            out.push(t('pf.err_web_in_phone', { op: opLabel(n.op) }));
        if (n.op === 'run_payout_flow' && !QUEUES.includes(n.task_type))
            out.push(t('pf.err_queue'));
        if (n.op === 'run_payout_flow' && !n.flow_id)
            out.push(t('pf.err_tasks_flow_pick'));
        // Empty is a choice here: the queue then comes from the caller, merchant by default.
        if (n.op === 'take_tasks' && n.task_type && !QUEUES.includes(n.task_type))
            out.push(t('pf.err_queue_take'));
        if (n.op === 'run_deposit_flow' && !n.flow_id)
            out.push(t('pf.err_deposit_flow_pick'));
        // A pick is a registry id, and the registry is per panel: an imported document
        // carries the ids (or the slugs) of the panel it came from, which resolve to
        // nothing here. Both the launch and a test run then ship the document WITHOUT the
        // flow embedded and the engine refuses the whole pass, so it is called out as a
        // problem of its own rather than passing the "something is picked" check.
        if (FLOW_PICK_KIND[n.op] && n.flow_id && !isFlowRef(n.flow_id)) {
            out.push(t('pf.err_flow_ref_imported', { op: opLabel(n.op), ref: String(n.flow_id) }));
        }
        if (USSD_OPS.includes(n.op))
            ussdProblems(n, out);
        if (COMMIT_OPS.includes(n.op))
            commits += 1;
        if (SUCCESS_OPS.includes(n.op))
            successChecks += 1;
    });
    nestingProblems(nodes, deposit, out);
    smsProblems(nodes, out);
    if (!deposit && !auto && !shift)
        takeTasksProblems(nodes, out);
    // The money rules belong to a payout document. An auto flow holds no steps of its own,
    // and the flows in its slots are checked as themselves.
    if (!deposit && !auto && !shift) {
        if (nodes.length && commits !== 1)
            out.push(t('pf.err_commit_count', { count: String(commits) }));
        // Nothing but a success check can report a payout as SUCCESS, so a flow without one
        // can only ever fail. The engine refuses to start it (flow_runner.validate) — say so
        // here, while the author is looking at the step it is missing from.
        if (nodes.length && !successChecks)
            out.push(t('pf.err_no_success_check'));
    }
    // A jump may only land on a TOP-LEVEL step (the engine enforces the same, so catch it
    // here rather than at launch).
    const seen = new Set();
    walkNodes(nodes, (n) => {
        if (!n.id)
            return;
        if (seen.has(n.id))
            out.push(t('pf.err_dup_label', { id: n.id }));
        seen.add(n.id);
    });
    const topLevel = new Set(nodes.filter((n) => n.id).map((n) => n.id));
    walkNodes(nodes, (n) => {
        if (n.op !== 'goto')
            return;
        if (!n.target)
            out.push(t('pf.err_goto_target'));
        else if (!topLevel.has(n.target))
            out.push(t('pf.err_goto_unknown', { target: n.target }));
    });
    return [...new Set(out)];
}
/** Every name the engine substitutes, for spotting a bare one in a `value`. */
const ALL_TOKENS = new Set([...TOKEN_GROUPS.flatMap((g) => g.tokens), ...VALUE_TOKENS,
    ...Object.keys(LEGACY_AMOUNT)]);
/**
 * Write bare token values in the braced form: `PIN` → `{PIN}`.
 *
 * The engine takes both — a `value` that IS a token name is resolved whole — but two
 * spellings of the same thing in one editor is a question nobody should have to ask, and
 * only the braced one works inside longer text. Behaviour is identical, so this runs on
 * whatever document is opened and lands with the next save.
 */
export function braceValueTokens(doc) {
    walkNodes(doc?.steps, (n) => {
        const v = typeof n.value === 'string' ? n.value.trim() : '';
        if (v && ALL_TOKENS.has(v))
            n.value = `{${v}}`;
        // One amount token with a format, everywhere the old per-spelling ones were. The
        // engine understands both, so this only settles what the author reads.
        for (const field of ['value', 'xpath', 'until', 'key_template', 'name', 'ussd']) {
            const text = n[field];
            if (typeof text !== 'string' || !text.includes('{AMOUNT'))
                continue;
            // `minors` and `raw` were two names for the same integer; `raw` is the one kept.
            n[field] = text.replace(/\{(AMOUNT_[A-Z_]+)\}/g, (whole, name) => (LEGACY_AMOUNT[name] ? `{${LEGACY_AMOUNT[name]}}` : whole))
                .replace(/\{AMOUNT:minors\}/g, '{AMOUNT:raw}');
        }
    });
}
/**
 * Everything a document opened from storage or from a file gets before it is edited: one
 * spelling for the tokens, and the queue step in its current shape. Both are behaviour-
 * preserving rewrites that land with the next save.
 */
export function normalizeLoadedFlow(doc) {
    braceValueTokens(doc);
    liftFetchTasksFlow(doc);
    splitUssdSmsWait(doc);
}
/**
 * The USSD transfer and the wait for its SMS as two steps: a `ussd_pay` that carried the
 * wait inline (`wait_sms` + the patterns) gets a `wait_sms` step right after it. The engine
 * runs both shapes the same. The dialog PIN and the balance divisor are dropped too — the
 * editor no longer offers them.
 */
export function splitUssdSmsWait(doc) {
    const split = (list) => {
        if (!Array.isArray(list))
            return;
        for (let i = 0; i < list.length; i += 1) {
            const n = list[i];
            for (const lane of ['then', 'else', 'do'])
                split(n[lane]);
            if (n.op === 'ussd' || n.op === 'ussd_pay') {
                delete n.ussd_pin;
                delete n.divisor;
            }
            if (n.op !== 'ussd_pay' || !('wait_sms' in n))
                continue;
            const on = !!n.wait_sms;
            const wait = { op: 'wait_sms' };
            for (const key of ['sms_success', 'sms_decline', 'sms_timeout']) {
                if (n[key] !== undefined)
                    wait[key] = n[key];
                delete n[key];
            }
            delete n.wait_sms;
            if (on)
                list.splice(i + 1, 0, wait);
        }
    };
    split(doc?.steps);
}
/** A fresh node with the defaults the engine assumes. */
export function newNode(op) {
    const node = { op };
    if (op === 'if_visible') {
        node.then = [];
        node.else = [];
        node.timeout = 5;
    }
    if (op === 'if_app_running' || op === 'if_account_cached') {
        node.then = [];
        node.else = [];
    }
    if (op === 'repeat') {
        node.do = [];
        node.times = 3;
    }
    if (op === 'wait') {
        node.timeout = 10;
        node.on_absent = 'CANCEL';
    }
    // Longer than a plain wait: this one watches for a screen the wallet renders after a
    // network round-trip, and it is the last thing standing between a paid transfer and a
    // task reported as failed.
    if (op === 'verify_success') {
        node.timeout = 20;
        node.on_absent = 'CANCEL';
    }
    if (op === 'outcome')
        node.outcome = 'CANCEL';
    if (op === 'tap' || op === 'commit')
        node.timeout = 15;
    // PayoutHub's defaults: the MTN MoMo Cameroon transfer, a minute per reply, and minor
    // units to whole francs; the SMS wait starts from GoIP's Cameroon patterns.
    if (op === 'ussd') {
        node.ussd = '';
        node.var = 'USSD_REPLY';
        node.timeout = 60;
        node.ussd_fail = [...USSD_FAIL_SYSTEM];
    }
    if (op === 'ussd_pay') {
        node.ussd = USSD_PAY_DEFAULT;
        node.country_code = '237';
        node.var = 'USSD_REPLY';
        node.timeout = 60;
        node.ussd_fail = [...USSD_FAIL_SYSTEM, ...USSD_FAIL_OPERATOR];
    }
    if (op === 'wait_sms') {
        node.sms_success = [...SMS_SUCCESS_DEFAULT];
        node.sms_decline = [...SMS_DECLINE_DEFAULT];
    }
    if (op === 'read_balance' || op === 'read_account') {
        node.attr = 'text';
        node.timeout = 10;
    }
    if (op === 'back')
        node.times = 1;
    // Pulling a half-collapsed sheet up is what this gesture is for, so that is its default;
    // a drag rather than a fling, or the list keeps moving under the next step.
    if (op === 'swipe') {
        node.from_y = 0.8;
        node.to_y = 0.3;
        node.duration = 600;
    }
    if (op === 'sleep') {
        node.min = 1;
        node.max = 2;
    }
    // Braced form everywhere, though the engine also takes a bare token name as the whole
    // value: one spelling in the editor is one thing less to wonder about.
    if (op === 'fill')
        node.value = '{ACCOUNT}';
    // A keypad only ever types an amount — base_page rejects anything but digits and a comma.
    if (op === 'keypad')
        node.value = '{AMOUNT}';
    // A picture keypad is a bank's login pad — it types the access code.
    if (op === 'keypad_img')
        node.value = '{PIN}';
    // The list walk: two scrolls is what the hand-written deposit pass did; the body of a
    // row usually opens it, so `{ROW_XPATH}` is what the first tap inside will want.
    if (op === 'for_each') {
        node.do = [];
        node.max_scrolls = 2;
    }
    if (op === 'read') {
        node.attr = 'text';
        node.timeout = 10;
    }
    // The commonest test on a row is on the row's own text.
    if (op === 'if_match') {
        node.then = [];
        node.else = [];
        node.value = '{ROW}';
    }
    if (op === 'if_new') {
        node.then = [];
        node.else = [];
    }
    // A fresh queue step plays the app's DEFAULT payout flow rather than pinning one: that is
    // the pick that keeps working after the next flow is authored, and it is a choice
    // somebody made rather than whatever was saved last.
    if (op === 'run_payout_flow')
        node.task_type = 'external_payout';
    // A flow's own request takes merchant payouts unless told otherwise.
    if (op === 'take_tasks')
        node.task_type = 'merchant_payout';
    // Written out rather than left to the step's own fallback: which wallet the deposit list
    // was asked about is the whole difference between "nothing is expected" and a history
    // walk that never happened, and an empty field says neither. The group and the bucket
    // stay empty — those are the device's, and a flow that pins them stops following it.
    if (op === 'take_deposits') {
        node.then = [];
        node.else = [];
        node.account_number = '{ACCOUNT_FROM}';
    }
    if (op === 'report_deposit') {
        node.attach_screenshot = true;
        node.key = '{DEPOSIT_KEY}';
        node.currency = 'ARS';
    }
    return node;
}
/** An empty document for a new flow. */
export function emptyFlowDoc(app) {
    return { app, version: null, steps: [] };
}
/** Whether a step's `flow_id` can name a flow at all: a registry id is a positive integer.
 *  Anything else (a slug from an exported document) resolves to nothing in this panel. */
export function isFlowRef(ref) {
    const n = Number(ref);
    return Number.isInteger(n) && n > 0;
}
/** The payout flows a document's `run_payout_flow` nodes play, by id. */
export function payoutFlowRefs(doc) {
    const refs = new Set();
    walkNodes(doc.steps, (n) => { if (n.op === 'run_payout_flow' && n.flow_id)
        refs.add(String(n.flow_id)); });
    return [...refs];
}
/**
 * The document as the ENGINE takes it: the record's kind stamped on, and the payout flows its
 * `run_payout_flow` nodes play embedded under `payout_flows`, keyed by the node's own `flow_id`
 * (each with its name, the way the launch ships a flow). The stored document never carries
 * either — the pick is a reference to a flow that keeps being edited on its own, resolved
 * when the run starts.
 *
 * The launch does the same server-side (db.ts), where the team assignment the editor has no
 * view of is weighed as well.
 */
export function embedPayoutFlows(doc, taskType, flows) {
    const payoutFlows = {};
    for (const ref of payoutFlowRefs(doc)) {
        const rec = flows.find((f) => f.id === Number(ref));
        if (rec)
            payoutFlows[ref] = { ...rec.flow, name: rec.name };
    }
    return {
        ...doc, task_type: taskType,
        ...(Object.keys(payoutFlows).length ? { payout_flows: payoutFlows } : {}),
    };
}
/** The flows a document's steps play, split by the kind each op's pick has to be. */
export function autoSlotRefs(doc) {
    const deposit = new Set();
    const payout = new Set();
    walkNodes(doc.steps, (n) => {
        const kind = FLOW_PICK_KIND[n.op];
        if (!kind || !n.flow_id)
            return;
        (kind === 'deposit' ? deposit : payout).add(String(n.flow_id));
    });
    return { deposit: [...deposit], payout: [...payout] };
}
/**
 * An auto document as the ENGINE takes it: the two kinds embedded under maps of their own,
 * because they are separate registries and an id from one says nothing about the other.
 *
 * A deposit flow runs UNCHANGED in an auto pass — its own queue steps included — so it is
 * embedded with the payout flows those steps play, exactly as a deposit run ships it.
 */
export function embedAutoFlows(doc, depositFlows, payoutFlows) {
    const refs = autoSlotRefs(doc);
    const embed = (list, from) => {
        const out = {};
        for (const ref of list) {
            const rec = from.find((f) => f.id === Number(ref));
            if (!rec)
                continue;
            out[ref] = from === depositFlows
                ? embedPayoutFlows({ ...rec.flow, name: rec.name }, 'deposit', payoutFlows)
                : { ...rec.flow, name: rec.name };
        }
        return out;
    };
    const deposits = embed(refs.deposit, depositFlows);
    const payouts = embed(refs.payout, payoutFlows);
    return {
        ...doc, task_type: 'auto',
        ...(Object.keys(deposits).length ? { deposit_flows: deposits } : {}),
        ...(Object.keys(payouts).length ? { payout_flows: payouts } : {}),
    };
}
/**
 * Rewrite a document written before the queue step named its own payout flow.
 *
 * Back then `run_payout_flow` carried a `then` lane and the flow was played by a `subflow` node
 * inside it. The lane is gone: the pick moves up onto the queue step, and everything else the
 * lane held (steps written around the payout flow) has no place to run any more, which is why
 * this drops it rather than pretending it still applies. Runs on load and lands with the next
 * save; the stored flows were rewritten once by a migration (panel/src/server/db.ts).
 */
export function liftFetchTasksFlow(doc) {
    walkNodes(doc?.steps, (n) => {
        // The step's old name, from before a payout flow could take its tasks itself.
        if (n.op === 'fetch_tasks')
            n.op = 'run_payout_flow';
        if (n.op !== 'run_payout_flow')
            return;
        if (!n.flow_id) {
            const sub = (n.then ?? []).find((c) => c.op === 'subflow' && c.flow_id);
            if (sub)
                n.flow_id = sub.flow_id;
        }
        delete n.then;
        delete n.else;
    });
}
// ── Fetchers ───────────────────────────────────────────────────────────────────
async function fail(res, fallback) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error ?? fallback);
}
/**
 * The flows a device on this farm may be pointed at — the farm's team's own plus the
 * global ones, which is exactly what the launch resolves. The registry list below is the
 * superadmin's view of EVERYTHING and would offer picks the launch then ignores.
 */
export async function fetchFarmFlows(farmId) {
    const res = await fetch(`/api/farms/${farmId}/clicker/flows`);
    if (!res.ok)
        await fail(res, t('pf.err_load'));
    return (await res.json()).flows;
}
export async function fetchPayoutFlows(filter) {
    const q = new URLSearchParams();
    if (filter?.app)
        q.set('app', filter.app);
    if (filter?.taskType)
        q.set('task_type', filter.taskType);
    const qs = q.toString() ? `?${q}` : '';
    const res = await fetch(`/api/clicker/payout-flows${qs}`);
    if (!res.ok)
        await fail(res, t('pf.err_load'));
    return (await res.json()).flows;
}
export async function fetchFlowScopes() {
    const res = await fetch('/api/clicker/payout-flow-scopes');
    if (!res.ok)
        await fail(res, t('pf.err_load'));
    return await res.json();
}
export async function createPayoutFlow(body) {
    const res = await fetch('/api/clicker/payout-flows', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (!res.ok)
        await fail(res, t('pf.err_save'));
    return (await res.json()).flow;
}
export async function updatePayoutFlow(id, patch) {
    const res = await fetch(`/api/clicker/payout-flows/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
    });
    if (!res.ok)
        await fail(res, t('pf.err_save'));
}
export async function deletePayoutFlow(id) {
    const res = await fetch(`/api/clicker/payout-flows/${id}`, { method: 'DELETE' });
    if (!res.ok)
        await fail(res, t('pf.err_delete'));
}
/**
 * Duplicate a flow. The server copies the steps but NOT the assignment — the clone lands
 * as an unassigned draft, so copying a live flow can never put a second flow in front of a
 * running device.
 */
export async function clonePayoutFlow(id) {
    const res = await fetch(`/api/clicker/payout-flows/${id}/clone`, { method: 'POST' });
    if (!res.ok)
        await fail(res, t('pf.err_save'));
    return (await res.json()).flow;
}
export async function fetchFlowHistory(id) {
    const res = await fetch(`/api/clicker/payout-flows/${id}/history`);
    if (!res.ok)
        await fail(res, t('pf.err_load'));
    return (await res.json()).versions;
}
/** Put a version back. The server records the restore as one more version. */
export async function restoreFlowVersion(id, versionId) {
    const res = await fetch(`/api/clicker/payout-flows/${id}/history/${versionId}/restore`, { method: 'POST' });
    if (!res.ok)
        await fail(res, t('pf.err_save'));
    return (await res.json()).flow;
}
export async function setPayoutFlowTeams(id, teamIds) {
    const res = await fetch(`/api/clicker/payout-flows/${id}/teams`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ team_ids: teamIds }),
    });
    if (!res.ok)
        await fail(res, t('pf.err_save'));
}
export async function startFlowRun(udid, flow, params) {
    const res = await fetch(`/api/devices/${encodeURIComponent(udid)}/flow-debug`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flow, params }),
    });
    if (!res.ok)
        await fail(res, t('pf.err_run'));
}
export async function flowRunCommand(udid, cmd) {
    const res = await fetch(`/api/devices/${encodeURIComponent(udid)}/flow-debug/command`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cmd),
    });
    if (!res.ok)
        await fail(res, t('pf.err_run'));
}
export async function pollFlowRun(udid, after) {
    const res = await fetch(`/api/devices/${encodeURIComponent(udid)}/flow-debug?after=${after}`);
    if (!res.ok)
        await fail(res, t('pf.err_run'));
    return await res.json();
}
export async function stopFlowRun(udid) {
    await fetch(`/api/devices/${encodeURIComponent(udid)}/flow-debug`, { method: 'DELETE' });
}
/**
 * Close the session from a page that is going away — a reload, a closed tab, a link out.
 *
 * `keepalive` is the whole point: a plain fetch is cancelled together with the document,
 * so a reload left the run going. And a run left going is not an idle session — it is a
 * flow still walking towards its commit with nobody watching, which the runner only ends
 * two minutes later when its idle sweep notices nobody is polling.
 */
export function stopFlowRunOnUnload(udid) {
    try {
        void fetch(`/api/devices/${encodeURIComponent(udid)}/flow-debug`, { method: 'DELETE', keepalive: true });
    }
    catch { /* the page is already going; there is nowhere left to report this */ }
}
export async function fetchUiDump(udid) {
    const res = await fetch(`/api/devices/${encodeURIComponent(udid)}/ui-dump`);
    if (!res.ok)
        await fail(res, t('pf.err_dump'));
    return await res.json();
}
/** The run stored for one operation, with the document its step paths belong to. */
export async function fetchOperationRun(opId) {
    const res = await fetch(`/api/operations/${opId}/run`);
    if (!res.ok)
        await fail(res, t('pf.err_run_not_found'));
    return await res.json();
}
